import { initializeApp, getApps, getApp } from 'firebase/app';
import { 
  getFirestore, 
  doc, 
  setDoc, 
  getDoc, 
  updateDoc, 
  collection, 
  query, 
  where,
  getDocs,
  onSnapshot,
  serverTimestamp,
  Timestamp,
  deleteDoc,
  orderBy,
  limit
} from 'firebase/firestore';
import { getAuth, signInAnonymously } from 'firebase/auth';

// Firebase configuration — shared singleton to avoid double-init with firebaseAuth.ts
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  databaseURL: process.env.REACT_APP_FIREBASE_DATABASE_URL,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID
};

// Initialize Firebase
const app = getApps().length > 0 ? getApp() : initializeApp(firebaseConfig);
const db = getFirestore(app);
const auth = getAuth(app);

// Battle room interfaces
export interface BattleRoom {
  id: string;
  name: string;
  creatorId: string;
  isPrivate: boolean;
  password?: string;
  status: 'waiting' | 'in-progress' | 'completed';
  players: BattlePlayer[];
  maxPlayers: number;
  text: string;
  createdAt: Timestamp;
  startedAt?: Timestamp;
  endedAt?: Timestamp;
}

export interface BattlePlayer {
  id: string;
  username: string;
  profilePic?: string;
  country: string;
  status: 'waiting' | 'ready' | 'playing' | 'finished';
  progress: number;
  wpm: number;
  accuracy: number;
  position: number | null;
  finishedAt?: Timestamp;
}

export interface BattleMessage {
  id: string;
  roomId: string;
  senderId: string;
  senderName: string;
  text: string;
  timestamp: Timestamp;
}

// Firebase Battle Service
export class FirebaseBattleService {
  private static instance: FirebaseBattleService;
  private userId: string | null = null;
  
  private constructor() {}
  
  public static getInstance(): FirebaseBattleService {
    if (!FirebaseBattleService.instance) {
      FirebaseBattleService.instance = new FirebaseBattleService();
    }
    return FirebaseBattleService.instance;
  }
  
  // Initialize with user ID
  public async init(userId: string): Promise<void> {
    this.userId = userId;
    
    // Sign in anonymously to Firebase (optional, depending on your auth strategy)
    try {
      await signInAnonymously(auth);
    } catch (error) {
      console.error("Error signing in anonymously to Firebase:", error);
    }
  }
  
  // Create a new battle room
  public async createBattleRoom(
    roomData: Partial<BattleRoom> & { name: string; creatorId: string }
  ): Promise<BattleRoom | null> {
    try {
      const roomId = Math.random().toString(36).substring(2, 10);
      
      const battleRoom: BattleRoom = {
        id: roomId,
        name: roomData.name,
        creatorId: roomData.creatorId,
        isPrivate: roomData.isPrivate || false,
        password: roomData.password,
        status: 'waiting',
        players: [],
        maxPlayers: roomData.maxPlayers || 2,
        text: roomData.text || '',
        createdAt: serverTimestamp() as Timestamp
      };
      
      await setDoc(doc(db, 'battleRooms', roomId), battleRoom);
      return battleRoom;
    } catch (error) {
      console.error("Error creating battle room:", error);
      return null;
    }
  }
  
  // Get battle room by ID
  public async getBattleRoom(roomId: string): Promise<BattleRoom | null> {
    try {
      const roomDoc = await getDoc(doc(db, 'battleRooms', roomId));
      
      if (roomDoc.exists()) {
        return roomDoc.data() as BattleRoom;
      }
      return null;
    } catch (error) {
      console.error("Error getting battle room:", error);
      return null;
    }
  }
  
  // Get all public battle rooms
  public async getPublicBattleRooms(): Promise<BattleRoom[]> {
    try {
      const q = query(
        collection(db, 'battleRooms'),
        where('isPrivate', '==', false),
        where('status', 'in', ['waiting', 'in-progress'])
      );
      
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => doc.data() as BattleRoom);
    } catch (error) {
      console.error("Error getting public battle rooms:", error);
      return [];
    }
  }
  
  // Join a battle room
  public async joinBattleRoom(
    roomId: string, 
    player: Omit<BattlePlayer, 'progress' | 'wpm' | 'accuracy' | 'position' | 'status' | 'finishedAt'>
  ): Promise<boolean> {
    try {
      const room = await this.getBattleRoom(roomId);
      
      if (!room) return false;
      
      if (room.players.length >= room.maxPlayers) {
        throw new Error("Room is full");
      }
      
      if (room.status !== 'waiting') {
        throw new Error("Battle already in progress or completed");
      }
      
      // Check if player is already in the room
      const existingPlayerIndex = room.players.findIndex(p => p.id === player.id);
      if (existingPlayerIndex >= 0) return true;
      
      const newPlayer: BattlePlayer = {
        ...player,
        progress: 0,
        wpm: 0,
        accuracy: 0,
        position: null,
        status: 'waiting'
      };
      
      await updateDoc(doc(db, 'battleRooms', roomId), {
        players: [...room.players, newPlayer]
      });
      
      return true;
    } catch (error) {
      console.error("Error joining battle room:", error);
      return false;
    }
  }
  
  // Leave a battle room
  public async leaveBattleRoom(roomId: string, playerId: string): Promise<boolean> {
    try {
      const room = await this.getBattleRoom(roomId);
      
      if (!room) return false;
      
      // Remove the player
      const updatedPlayers = room.players.filter(p => p.id !== playerId);
      
      // If room is empty, delete it
      if (updatedPlayers.length === 0) {
        await deleteDoc(doc(db, 'battleRooms', roomId));
        return true;
      }
      
      // If creator left, assign a new creator
      let updatedRoom: Partial<BattleRoom> = {
        players: updatedPlayers
      };
      
      if (room.creatorId === playerId && updatedPlayers.length > 0) {
        updatedRoom.creatorId = updatedPlayers[0].id;
      }
      
      await updateDoc(doc(db, 'battleRooms', roomId), updatedRoom);
      return true;
    } catch (error) {
      console.error("Error leaving battle room:", error);
      return false;
    }
  }
  
  // Update player status in a room
  public async updatePlayerStatus(
    roomId: string, 
    playerId: string, 
    status: BattlePlayer['status']
  ): Promise<boolean> {
    try {
      const room = await this.getBattleRoom(roomId);
      
      if (!room) return false;
      
      const updatedPlayers = room.players.map(player => {
        if (player.id === playerId) {
          return {
            ...player,
            status,
            finishedAt: status === 'finished' ? serverTimestamp() as Timestamp : player.finishedAt
          };
        }
        return player;
      });
      
      await updateDoc(doc(db, 'battleRooms', roomId), {
        players: updatedPlayers
      });
      
      // If all players are ready, update room status to in-progress
      if (status === 'ready' && updatedPlayers.every(p => p.status === 'ready')) {
        await updateDoc(doc(db, 'battleRooms', roomId), {
          status: 'in-progress',
          startedAt: serverTimestamp()
        });
      }
      
      // If all players are finished, update room status to completed
      if (status === 'finished' && updatedPlayers.every(p => p.status === 'finished')) {
        // Sort players by their finishedAt timestamp to determine positions
        const sortedPlayers = [...updatedPlayers]
          .sort((a, b) => {
            if (!a.finishedAt) return 1;
            if (!b.finishedAt) return -1;
            return a.finishedAt.toMillis() - b.finishedAt.toMillis();
          })
          .map((player, index) => ({
            ...player,
            position: index + 1
          }));
        
        await updateDoc(doc(db, 'battleRooms', roomId), {
          status: 'completed',
          endedAt: serverTimestamp(),
          players: sortedPlayers
        });
      }
      
      return true;
    } catch (error) {
      console.error("Error updating player status:", error);
      return false;
    }
  }
  
  // Update player progress during a battle
  public async updatePlayerProgress(
    roomId: string, 
    playerId: string, 
    progress: number,
    wpm: number,
    accuracy: number
  ): Promise<boolean> {
    try {
      const roomRef = doc(db, 'battleRooms', roomId);
      const room = await getDoc(roomRef);
      
      if (!room.exists()) return false;
      
      const roomData = room.data() as BattleRoom;
      const updatedPlayers = roomData.players.map(player => {
        if (player.id === playerId) {
          return {
            ...player,
            progress,
            wpm,
            accuracy
          };
        }
        return player;
      });
      
      await updateDoc(roomRef, {
        players: updatedPlayers
      });
      
      return true;
    } catch (error) {
      console.error("Error updating player progress:", error);
      return false;
    }
  }
  
  // Send a message in the battle chat
  public async sendBattleMessage(
    roomId: string, 
    senderId: string, 
    senderName: string, 
    text: string
  ): Promise<boolean> {
    try {
      const messageId = Math.random().toString(36).substring(2, 15);
      
      await setDoc(doc(db, 'battleMessages', messageId), {
        id: messageId,
        roomId,
        senderId,
        senderName,
        text,
        timestamp: serverTimestamp()
      });
      
      return true;
    } catch (error) {
      console.error("Error sending battle message:", error);
      return false;
    }
  }
  
  // Get recent completed battles
  public async getRecentCompletedBattles(limitCount: number = 5): Promise<BattleRoom[]> {
    try {
      const q = query(
        collection(db, 'battleRooms'),
        where('status', '==', 'completed'),
        orderBy('endedAt', 'desc'),
        limit(limitCount)
      );
      
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => doc.data() as BattleRoom);
    } catch (error) {
      console.error("Error fetching recent battles:", error);
      return [];
    }
  }
  
  // Subscribe to battle room changes
  public subscribeToBattleRoom(
    roomId: string, 
    callback: (room: BattleRoom | null) => void
  ): () => void {
    const unsubscribe = onSnapshot(
      doc(db, 'battleRooms', roomId),
      (doc) => {
        if (doc.exists()) {
          callback(doc.data() as BattleRoom);
        } else {
          callback(null);
        }
      },
      (error) => {
        console.error("Error subscribing to battle room:", error);
        callback(null);
      }
    );
    
    return unsubscribe;
  }
  
  // Subscribe to battle chat
  public subscribeToBattleChat(
    roomId: string,
    callback: (messages: BattleMessage[]) => void
  ): () => void {
    const q = query(
      collection(db, 'battleMessages'),
      where('roomId', '==', roomId)
    );
    
    const unsubscribe = onSnapshot(
      q,
      (querySnapshot) => {
        const messages = querySnapshot.docs
          .map(doc => doc.data() as BattleMessage)
          .sort((a, b) => a.timestamp.toMillis() - b.timestamp.toMillis());
        
        callback(messages);
      },
      (error) => {
        console.error("Error subscribing to battle chat:", error);
        callback([]);
      }
    );
    
    return unsubscribe;
  }
}

// Export a singleton instance
export const firebaseBattleService = FirebaseBattleService.getInstance();
